import RecipientIcon from '@assets/icons/user.svg?react'
import {
  CoreOnboardingAnnounce,
  CorePaymentInput,
  CoreSelect,
  MMButton,
  RecurringToggleBox,
  SendMoneyMethodInfo,
  SuccessPane
} from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import { LivenessContext } from '@context/LivenessContext'
import { toastSuccess } from '@utils'
import CryptoRecipientModal from '@views/recipients/CryptoRecipientModal/CryptoRecipientModal'
import AccountingCategorySelect from '@withdrawals/components/AccountingCategorySelect'
import NoSuitableAccountModal from '@withdrawals/components/NoSuitableAccountModal'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import CryptoTransferPreview from '@withdrawals/views/CryptoTransferPreview'
import {
  extractOptionStatusFromAccount,
  MerchantAccountTypes,
  PaymentOptionStatuses,
  paths,
  responseContainsLiveness,
  responseDoesNotContainsChallenge,
  selectMerchantAccountType,
  useAppSelector,
  useCryptoTransferQry,
  useReturnToOrigin
} from 'mmfintech-backend-api'
import { GlobalContext, isValidObject, isValidString, OtpContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

interface LocationState {
  preselectCurrency?: string
}

const CryptoTransfer = () => {
  const { modalHide, modalShow } = useContext(GlobalContext)
  const { setOtpOnSuccess } = useContext(OtpContext)
  const { setLivenessOnSuccess } = useContext(LivenessContext)

  const { currentStep, setCurrentStep } = useHeaderContext()

  const recipientId = new URLSearchParams(window.location.search).get('recipientId')

  const { state } = useLocation<LocationState>() || {}

  const [result, setResult] = useState(null)
  const [promoCode, setPromoCode] = useState('') // Stored for final submission

  const history = useHistory()
  const accountType = useAppSelector(selectMerchantAccountType)

  const { returnToOrigin } = useReturnToOrigin()
  const returnToOriginUrl = returnToOrigin({ recipientId })

  const {
    formValues,
    handleRecipientChange,
    handlePreview,
    handleInitiate,
    selectedAccount,
    selectedRecipient,
    setSelectedAccount,
    filteredRecipients,
    accounts,
    accountsError,
    accountsFetching,
    recipientsError,
    recipientsFetching,
    reloadRecipients,
    previewError,
    previewFetching,
    resetMutations,
    shouldShowWarning,
    isRecurringOpen,
    setIsRecurringOpen
  } = useCryptoTransferQry({
    editRecipient: () => editRecipient(),
    onPreviewSuccess: () => setCurrentStep(2),
    onInitiateSuccess: (payload: any) => handleInitiateSuccess(payload),
    selectAddNew: false,
    validation: 'float',
    recipientId: parseInt(recipientId, 10),
    preselectCurrency: state?.preselectCurrency
  })

  const editRecipient = (recipient?: any): void => {
    const { currencyCode } = selectedAccount || {}
    if (currencyCode?.length) {
      modalShow({
        header: recipient?.paymentInstrumentId
          ? tr('FRONTEND.RECIPIENTS.EDIT.TITLE', 'Recipient details')
          : tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
        content: (
          <CryptoRecipientModal
            recipientId={recipient?.paymentInstrumentId}
            currencyCode={currencyCode}
            onClose={modalHide}
            onSuccess={(response: any) => {
              reloadRecipients(response?.paymentInstrumentId)
              toastSuccess(
                recipient?.paymentInstrumentId
                  ? tr('FRONTEND.RECIPIENTS.EDIT.SUCCESS', 'Recipient updated.')
                  : tr('FRONTEND.RECIPIENTS.CREATE.SUCCESS', 'Recipient successfully created'),
                undefined,
                {
                  duration: 3_000
                }
              )
            }}
          />
        )
      })
    } else {
      modalShow({
        header: tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
        content: <NoSuitableAccountModal />,
        options: { size: 'medium', closeOnClickOutside: false }
      })
    }
  }

  const hasValidRecipientPaymentOption = () =>
    selectedRecipient?.paymentOption?.status === PaymentOptionStatuses.AVAILABLE

  const hasValidPaymentOption = () =>
    hasValidRecipientPaymentOption() ||
    extractOptionStatusFromAccount(selectedAccount) === PaymentOptionStatuses.AVAILABLE

  const showResult = (data: any) => {
    setResult(data)
    setCurrentStep(3)
  }

  const transferSuccess = (data: any) => {
    // A liveness check means the withdrawal isn't finalized yet — the global
    // liveness modal takes over until the user completes it on the partner site.
    // Stash the withdrawal result now and reveal it once liveness passes; the
    // terminal `/status` response itself carries no withdrawal detail, so we
    // show the data we already have rather than the callback argument.
    if (responseContainsLiveness(data)) {
      setResult(data)
      setLivenessOnSuccess(() => () => setCurrentStep(3))
      return
    }
    showResult(data)
  }

  const handleInitiateSuccess = (data: any): void => {
    if (responseDoesNotContainsChallenge(data)) {
      transferSuccess(data)
    }
  }

  const handleSubmitInitiate = () => {
    setOtpOnSuccess(() => transferSuccess)
    handleInitiate(promoCode)
  }

  useEffect(() => {
    return () => {
      setCurrentStep(1)
      setResult(null)
      resetMutations()
      // Don't leave a pending callback pointing at an unmounted screen.
      setLivenessOnSuccess(null)
    }
  }, [])

  return (
    <>
      {currentStep === 1 && (
        <div className={styles['send-money-container']} data-test='crypto-transfer-container'>
          <div className='from-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.CRYPTO.FROM', 'From')}</div>
            </div>
            <CorePaymentInput
              selectedOption={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              dataTest={'deposit-payment-input-test'}
              options={accounts}
              enableMaxAmountButton
              maxInputAmount={selectedRecipient?.paymentOption?.maxAmount}
              minInputAmount={selectedRecipient?.paymentOption?.minAmount}
              onChange={(value: any) => formValues.setValue('amount', value)}
              value={formValues.getValue('amount')}
              error={formValues.getError('amount')}
              isLoading={accountsFetching}
              hideCurrencySymbol={true}
              noOptionsMessage={tr(
                'FRONTEND.WITHDRAW.CRYPTO.NO_ACCOUNTS_WITH_BALANCE',
                'No accounts with enough balance'
              )}
              noOptionsAction={{
                label: tr('FRONTEND.WITHDRAW.NO_ACCOUNT.BUTTON_DEPOSIT', 'Deposit'),
                onClick: () => history.push(paths.banking.deposit())
              }}
            />
          </div>

          <div className='to-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.CRYPTO.TO', 'To')}</div>

              {!recipientId && (
                <MMButton
                  type='button'
                  size='normal'
                  variant='tertiary'
                  leftIcon={RecipientIcon}
                  caption={tr('FRONTEND.RECIPIENTS.LIST.BUTTON_ADD', 'New recipient')}
                  onClick={() => editRecipient()}
                  loading={previewFetching}
                  data-test='button-add-recipient'
                />
              )}
            </div>

            <CoreSelect
              type='default'
              data-test='destination-payment-instrument-id'
              label={tr('FRONTEND.WITHDRAW.CRYPTO.RECIPIENT', 'Recipient')}
              options={filteredRecipients}
              onChange={handleRecipientChange}
              loading={recipientsFetching}
              disabled={recipientsFetching}
              value={
                selectedRecipient
                  ? {
                      value: selectedRecipient?.paymentInstrumentId,
                      label: selectedRecipient?.name
                    }
                  : null
              }
              LeftIcon={<RecipientIcon />}
              noOptionsMessage={tr('FRONTEND.WITHDRAW.CRYPTO.NO_RECIPIENTS', 'No recipients created')}
            />
            {isValidObject(selectedRecipient) && isValidString(selectedRecipient.description) && (
              <div className='recipient-info-container'>
                <div className='recipient-description-wrapper'>
                  <div className='description-label'>
                    {tr('FRONTEND.WITHDRAW.CRYPTO.RECIPIENT_DESCRIPTION', 'Recipient description')}
                  </div>
                  <div className='description'>{selectedRecipient.description}</div>
                </div>
              </div>
            )}
          </div>

          {isValidObject(selectedRecipient?.paymentOption) && (
            <SendMoneyMethodInfo
              paymentOption={selectedRecipient.paymentOption}
              currencyCode={selectedAccount?.currencyCode}
            />
          )}

          <AccountingCategorySelect formValues={formValues} />

          <RecurringToggleBox
            onToggle={() => setIsRecurringOpen(!isRecurringOpen)}
            isRecurringOpen={isRecurringOpen}
            recurringDetailsFormValues={formValues}
          />

          {shouldShowWarning() ||
            (accountType === MerchantAccountTypes.PROSPECT && !hasValidPaymentOption() && <CoreOnboardingAnnounce />)}

          <div className='form-actions'>
            <ErrorDisplay error={[accountsError, recipientsError, previewError]} />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              onClick={handlePreview}
              loading={previewFetching}
              disabled={
                accountsFetching ||
                recipientsFetching ||
                previewFetching ||
                selectedRecipient?.paymentOption?.status !== PaymentOptionStatuses.AVAILABLE ||
                formValues.getFloat('amount') <= 0
              }
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <CryptoTransferPreview
          selectedAccount={selectedAccount}
          selectedRecipient={selectedRecipient}
          onSubmit={handleSubmitInitiate}
          onCancel={() => setCurrentStep(1)}
          formValues={formValues}
          onApplyPromoCode={setPromoCode}
        />
      )}

      {currentStep === 3 && <SuccessPane response={result} onClick={() => history.push(returnToOriginUrl)} />}
    </>
  )
}

export default CryptoTransfer
